import React, { useState } from "react";
import { Box, Divider, InputLabel, Slider, SliderThumb, styled, TextField } from "@mui/material";
import colors from "../../../shared/themes/colors";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../../../shared/themes/redesignTokens";

interface PriceSliderThumbProps extends React.HTMLAttributes<unknown> { }

const PriceSliderThumb = (props: PriceSliderThumbProps) => {
  const { children, ...other } = props;
  return (
    <SliderThumb {...other}>
      {children}
      <span className="price-bar" />
    </SliderThumb>
  );
};

const PriceSlider = styled(Slider)(({ theme }) => ({
  color: dt.blue,
  height: 3,
  padding: "13px 0",
  "& .MuiSlider-thumb": {
    height: 21,
    width: 21,
    backgroundColor: "#fff",
    border: "4px solid currentColor",
    "& .price-bar": {
      height: 9,
      width: 9,
      borderRadius: "50%",
      backgroundColor: "currentColor",
      color: "#332B41"
    }
  },
  "& .MuiSlider-track": {
    height: 6
  },
  "& .MuiSlider-rail": {
    color: "#C0DBFB",
    opacity: 1,
    height: 8
  }
}));

export interface PriceRangeFilterProps {
  value: number[];
  min?: number;
  max?: number;
  onChange?: (value: number[]) => void;
}

const PriceRangeFilter = ({ value, min, max, onChange }: PriceRangeFilterProps) => {
  const [priceRangeValue, setPriceRangeValue] = useState(value);

  const isValidParameters = min !== undefined && min !== null && max !== undefined && max !== null;
  const { translate } = useTranslation("marketplace");
  const { translate: tCommon } = useTranslation("common");

  const handleSliderChange = (newValue: number[]) => {
    setPriceRangeValue(newValue);
  };

  const handleMinInputChange = (minValue?: number) => {
    setPriceRangeValue([minValue!, priceRangeValue[1]]);
  };

  const handleMaxInputChange = (maxValue?: number) => {
    setPriceRangeValue([priceRangeValue[0], maxValue!]);
  };

  const handleApplyChanges = () => {
    if (!isValidParameters) {
      return;
    }

    const minValue = Math.max(Math.min(priceRangeValue[0], priceRangeValue[1], max), min);
    const maxValue = Math.min(Math.max(minValue, priceRangeValue[1]), max);
    const newPriceRangeValue = [minValue, maxValue];
    setPriceRangeValue(newPriceRangeValue);

    if (value[0] !== minValue || value[1] !== maxValue) {
      onChange?.(newPriceRangeValue);
    }
  };

  return (
    <>
      {isValidParameters && (
        <Box>
          <Box className="text-14-medium" color="#333333">
            {tCommon("priceEuro")}
          </Box>

          <Box className="flex justify-center mt-2">
            <PriceSlider
              sx={{ width: "90%" }}
              slots={{ thumb: PriceSliderThumb }}
              valueLabelDisplay="auto"
              disableSwap
              min={min}
              max={max}
              value={priceRangeValue}
              onChange={(ev, value) => handleSliderChange(value as number[])}
              onChangeCommitted={handleApplyChanges}
            />
          </Box>

          <Box className="flex gap-1 mt-2">
            <Box>
              <InputLabel>{translate("from")}</InputLabel>
              <TextField
                type="number"
                variant="outlined"
                placeholder={`${min}€`}
                sx={{
                  width: "100px",
                  ".MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: "36px !important"
                  }
                }}
                value={priceRangeValue[0] || undefined}
                onChange={(e) => handleMinInputChange(Number(e.target.value))}
                onKeyDown={(e) => e.key === "Enter" && handleApplyChanges()}
                onBlur={handleApplyChanges}
              />
            </Box>

            <Box>
              <InputLabel>{translate("to")}</InputLabel>
              <TextField
                type="number"
                variant="outlined"
                placeholder={`${max}€`}
                sx={{
                  width: "100px",
                  ".MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: "36px !important"
                  }
                }}
                value={priceRangeValue[1] || undefined}
                onChange={(e) => handleMaxInputChange(Number(e.target.value))}
                onKeyDown={(e) => e.key === "Enter" && handleApplyChanges()}
                onBlur={handleApplyChanges}
              />
            </Box>
          </Box>

          <Box className="w-full mt-4 mb-2">
            <Divider />
          </Box>
        </Box>
      )}
    </>
  );
};

export default PriceRangeFilter;